import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { useFormatters } from '../../../hooks';
import { addDriverPayment } from '../../../redux/Users/actions';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '../../../ui/core';
import SelectWithDescription from '../../../ui/core/SelectWithDescription';
import { CloseIcon } from '../../../ui/icons';
import { DriverCompensationReasons } from '../../../consts';

const MAX_PAYMENT_AMOUNT = 100;
const REASON_OTHER_ID = 'OTHER';

const formDefaultValues = {
  amount: '',
  selectedReason: {
    id: '',
    description: '',
  },
};

// Issue-only dialog. Reversals are handled per-payment from the payment log via
// DriverPaymentReverseDialog, so there is no arbitrary amount-based reduction.
function DriverBalanceAddPaymentDialog({ isOpen, onClose, onAdjusted, user, currentBalance = 0 }) {
  const f = useFormatters();
  const dispatch = useDispatch();
  const [submitError, setSubmitError] = useState(null);
  // Snapshot the balance when the dialog opens. Using the live prop here caused
  // a flash: on submit we refetch the balance (which already includes the new
  // payment), and the still-open dialog would briefly recompute
  // "new balance = updatedBalance + amount", double-counting the amount.
  const [openingBalance, setOpeningBalance] = useState(currentBalance);
  useEffect(() => {
    if (isOpen) {
      setOpeningBalance(currentBalance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        amount: yup
          .number()
          .typeError('Enter a valid amount')
          .moreThan(0, 'Amount must be greater than $0')
          .max(MAX_PAYMENT_AMOUNT, `Amount must not be higher than ${f.number.currency(MAX_PAYMENT_AMOUNT)}`)
          .required('Amount is required'),
        selectedReason: yup
          .object()
          .shape({
            id: yup.string().required('Reason is required'),
            description: yup.string().required('Description is required'),
          })
          .typeError('Reason is required'),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: structuredClone(formDefaultValues),
    resolver: yupResolver(validationSchema),
  });

  const currentFormValues = useWatch({ control: form.control });
  const { isSubmitting, isValid } = useFormState({ control: form.control });

  useEffect(() => {
    if (submitError) {
      const timer = setTimeout(() => setSubmitError(null), 4_000);
      return () => clearTimeout(timer);
    }
  }, [submitError]);

  const amountNumber = Number(currentFormValues.amount) || 0;
  const newBalance = openingBalance + amountNumber;

  function handleClose() {
    onClose();
    setTimeout(() => form.reset(formDefaultValues), 300);
  }

  async function onSubmit(dataToSubmit) {
    const { amount, selectedReason } = dataToSubmit;

    try {
      const result = await addDriverPayment({
        userId: user.id,
        amount: Number(amount),
        reason: selectedReason.id,
        description: selectedReason.description,
      })(dispatch);

      if (result.success) {
        if (onAdjusted) {
          await onAdjusted();
        }
        handleClose();
      } else {
        const backendMessage = result.error?.response?.data?.message || result.error?.message;
        setSubmitError(backendMessage || 'Could not add payment. Please try again later.');
      }
    } catch (error) {
      setSubmitError('Could not add payment. Please try again later.');
      console.error(error);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      sx={{ userSelect: 'none' }}
      component="form"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
        <DialogTitle sx={{ padding: 0 }}>Add Payment</DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Stack spacing="24px" sx={{ paddingTop: '8px' }}>
          <Typography variant="body2" color="text.secondary">
            Adds a one-time payment to {user.firstName || 'this driver'}&rsquo;s Stripe balance. This moves real funds
            and is logged. It can be reversed later from the payment log.
          </Typography>

          <Box>
            <TextField
              fullWidth
              autoFocus
              label="Amount"
              placeholder="0.00"
              error={!!form.formState.errors.amount}
              helperText={form.formState.errors.amount?.message}
              value={currentFormValues.amount}
              onChange={(e) => {
                const value = e.target.value;
                const centsLength = value.split('.')[1]?.length || 0;
                if (centsLength > 2) return;
                form.setValue('amount', value, { shouldValidate: true });
              }}
              InputProps={{
                startAdornment: <Typography sx={{ marginRight: '4px', opacity: 0.7 }}>$</Typography>,
              }}
              inputProps={{
                type: 'number',
                inputMode: 'decimal',
                step: 0.01,
                min: 0,
                max: MAX_PAYMENT_AMOUNT,
              }}
            />
          </Box>

          <SelectWithDescription
            alwaysShowDescription
            control={form.control}
            focusDescription
            id="driver-adjust-reason-label-id"
            labelDescription="Reason"
            labelId="driver-adjust-reason-label"
            name="selectedReason"
            options={DriverCompensationReasons}
            otherOptionId={REASON_OTHER_ID}
            otherOptionLabel="Description"
            placeholder="Select Reason"
            showOtherOption
          />

          <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', padding: '16px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Current balance
              </Typography>
              <Typography variant="body2">{f.number.currency(openingBalance)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt="8px">
              <Typography variant="subtitle2">New balance</Typography>
              <Typography variant="subtitle1">{f.number.currency(newBalance)}</Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: '0 32px 32px 32px' }}>
        {submitError ? (
          <Alert severity="error" sx={{ width: '100%' }}>
            {submitError}
          </Alert>
        ) : (
          <Button type="submit" variant="contained" fullWidth color="primary" disabled={isSubmitting || !isValid}>
            {isSubmitting ? 'Adding...' : `Add ${amountNumber > 0 ? f.number.currency(amountNumber) : 'Payment'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default DriverBalanceAddPaymentDialog;
